import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Add } from '@mui/icons-material';
import { quoteAPI } from 'modules/quote';
import { useModal } from 'hooks/useModal';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { ModalsKeys, Nullable } from 'global';
import { Button, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  useEquipmentActions,
  useEquipmentAssets,
  useEquipmentDelivery,
  EquipmentDeliveryForm,
  EquipmentDelivery,
  Section,
} from 'modules/equipment';
import { useEquipmentSearch } from '../../../hooks';
import { DealBuilderEquipmentSource, DealBuilderFormValues, DealBuilderIds, StepComponentProps } from '../../../types';
import { DeliverySubmitRef, useDealBuilderContext } from '../../../context';
import {
  dealBuilderApi,
  UpdateDealBuilderQuoteResponse,
  UpdateDealBuilderQuoteVariables,
  UpsertDealBuilderEquipmentsResponse,
  UpsertDealBuilderEquipmentsVariables,
} from '../../../api';
import {
  mapEquipmentSourceToItem,
  mapEquipmentItemToUpdateQuoteEquipmentInput,
  mapQuoteToUpdateQuoteInput,
  syncQuoteEquipmentWithItem,
  isSameDeliveryAddress,
} from '../../../lib';
import {
  DEAL_BUILDER_EQUIPMENT_ACTION_ADD,
  DEAL_BUILDER_EQUIPMENT_ACTION_EDIT,
  DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION,
  DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID,
} from '../../../constants';
import { EquipmentSearchBar } from './EquipmentSearchBar';
import { EquipmentItemCard } from './EquipmentItemCard';

export const EquipmentStep = ({ navigationNode }: StepComponentProps) => {
  const { control, setValue } = useFormContext<DealBuilderFormValues>();

  const deliveryAddressSubmitRef = useRef<Nullable<DeliverySubmitRef>>(null);

  const selectedId = useWatch({ control, name: 'equipment.selectedId' });
  // The deep-link effect and the dialog callback outlive the render they were created in
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const { search, results, isActive: isSearchActive, isSearching } = useEquipmentSearch();

  const {
    activeEntity,
    setActiveEntity,
    sourceData: { vo, equipments: equipmentData },
    equipment: { selectedEquipmentItem, setSelectedEquipmentItem, setEquipmentSaving, isEquipmentSaving },
  } = useDealBuilderContext();
  const activeEntityRef = useRef(activeEntity);
  activeEntityRef.current = activeEntity;
  const { handleOpen: openEquipmentDialog, handleClose: closeEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );

  const { onCreateEquipment, onUpdateEquipment } = useEquipmentActions();
  const { handleUploadImages } = useEquipmentAssets();
  const client = useApolloClient();
  const { replace } = useHistory();
  const location = useLocation();
  const lastSavedAddressRef = useRef<EquipmentDelivery | null>(null);
  const { handleUpdateDeliveryAddress } = useEquipmentDelivery(selectedId ?? undefined, address => {
    lastSavedAddressRef.current = address;
  });

  const [fetchEquipmentById] = useLazyQuery<{ equipment: DealBuilderEquipmentSource }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById,
    { fetchPolicy: 'network-only' }
  );

  const [upsertEquipmentsMutation] = useMutation<
    UpsertDealBuilderEquipmentsResponse,
    UpsertDealBuilderEquipmentsVariables
  >(dealBuilderApi.upsertDealBuilderEquipments);

  const [updateQuoteMutation, { loading: isLoading, error }] = useMutation<
    UpdateDealBuilderQuoteResponse,
    UpdateDealBuilderQuoteVariables
  >(dealBuilderApi.updateDealBuilderQuote, { refetchQueries: [quoteAPI.getQuoteCalcById], awaitRefetchQueries: true });

  const handleSaveEquipmentStep = async ({ proposalId, quoteId }: DealBuilderIds) => {
    if (!selectedEquipmentItem) return;

    setEquipmentSaving(true);

    try {
      const isQuoteExist = activeEntity?.quote;
      const shouldUpdateQuote = !!(selectedEquipmentItem && isQuoteExist && vo?.vendorOpportunityId);

      const [, updateQuoteResult] = await Promise.all([
        upsertEquipmentsMutation({
          variables: {
            input: {
              proposalId,
              equipmentIds: [selectedEquipmentItem.equipment.id],
            },
          },
        }),
        shouldUpdateQuote
          ? updateQuoteMutation({
              variables: {
                quoteId,
                input: mapQuoteToUpdateQuoteInput(
                  activeEntity.quote,
                  mapEquipmentItemToUpdateQuoteEquipmentInput(selectedEquipmentItem),
                  vo!.vendorOpportunityId
                ),
              },
            })
          : Promise.resolve(null),
        onUpdateEquipment({
          quantity: 1,
          equipmentId: selectedEquipmentItem.equipment.id,
          vendorOpportunityId: vo?.vendorOpportunityId ?? '',
          dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
        }),
      ]);

      const updatedQuote = updateQuoteResult?.data?.dealBuilder?.updateDealBuilderQuote?.entity;

      if (updatedQuote) setActiveEntity({ ...activeEntity!, quote: updatedQuote });

      if (!selectedId || !deliveryAddressSubmitRef.current) return;

      const result = await deliveryAddressSubmitRef.current.submit();

      if (!result.isValid) return;

      if (isSameDeliveryAddress(lastSavedAddressRef.current, result.formValues)) return;

      await handleUpdateDeliveryAddress(result.formValues);

      lastSavedAddressRef.current = result.formValues;
    } finally {
      setEquipmentSaving(false);
    }
  };

  // reset the tracked address when a different equipment item is selected
  useEffect(() => {
    lastSavedAddressRef.current = null;
  }, [selectedId]);

  const prepopulatedItems = useMemo(() => (equipmentData ?? []).map(mapEquipmentSourceToItem), [equipmentData]);

  const displayItems = useMemo(() => {
    // While searching, show only what the search returned (the selected item is not pinned)
    if (isSearchActive) return results;

    if (selectedEquipmentItem && !prepopulatedItems.some(i => i.equipment.id === selectedEquipmentItem.equipment.id)) {
      return [selectedEquipmentItem, ...prepopulatedItems];
    }
    return prepopulatedItems;
  }, [isSearchActive, results, prepopulatedItems, selectedEquipmentItem]);

  const handleSelect = (id: string) => {
    if (selectedId === id) {
      return;
    } else {
      const item = displayItems.find(i => i.equipment.id === id) ?? null;
      setValue('equipment.selectedId', id);
      setSelectedEquipmentItem(item);
    }
  };

  const handleViewUnit = (equipmentId: string) => {
    handleEditEquipment(equipmentId);
  };

  const handleAddManualUnit = () => {
    let imagesToUpload: File[] = [];

    openEquipmentDialog({
      equipmentId: undefined,
      onCancel: closeEquipmentDialog,
      onImagesSaved: (files: File[]) => {
        imagesToUpload = files;
      },
      onSubmit: async ({ createDTO }) => {
        closeEquipmentDialog();
        const equipmentId = await onCreateEquipment({
          vendorOpportunityId: vo?.vendorOpportunityId ?? '',
          dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
          ...createDTO,
        });

        if (!equipmentId || !imagesToUpload.length) return;

        await handleUploadImages(equipmentId, 'IMAGE', imagesToUpload);
        await client.refetchQueries({ include: ['EquipmentsByVOId'] });
      },
    });
  };

  const handleEditEquipment = useCallback(
    (equipmentId: string) => {
      openEquipmentDialog({
        equipmentId,
        onCancel: closeEquipmentDialog,
        onSubmit: async ({ updateDTO }) => {
          closeEquipmentDialog();
          setEquipmentSaving(true);
          try {
            const updatedId = await onUpdateEquipment({
              vendorOpportunityId: vo?.vendorOpportunityId ?? '',
              dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
              ...updateDTO,
            });

            if (updatedId !== selectedIdRef.current) return;
            const { data } = await fetchEquipmentById({ variables: { id: updatedId } });
            if (!data?.equipment) return;

            const updatedItem = mapEquipmentSourceToItem(data.equipment);
            setSelectedEquipmentItem(updatedItem);

            // Covers jumping to Pricing via the stepper without Continue, which would leave the old price on the quote line
            const entity = activeEntityRef.current;
            const quoteEquipment = entity?.quote?.equipment?.find(e => e.equipmentId === updatedId);
            if (!entity?.quote || !quoteEquipment || !vo?.vendorOpportunityId) return;

            const result = await updateQuoteMutation({
              variables: {
                quoteId: entity.quote.quoteId,
                input: mapQuoteToUpdateQuoteInput(
                  entity.quote,
                  syncQuoteEquipmentWithItem(quoteEquipment, updatedItem),
                  vo.vendorOpportunityId
                ),
              },
            });
            const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;
            if (updatedQuote) setActiveEntity({ ...(activeEntityRef.current ?? entity), quote: updatedQuote });
          } finally {
            setEquipmentSaving(false);
          }
        },
      });
    },
    [
      openEquipmentDialog,
      closeEquipmentDialog,
      onUpdateEquipment,
      fetchEquipmentById,
      setSelectedEquipmentItem,
      updateQuoteMutation,
      setActiveEntity,
      setEquipmentSaving,
      vo,
    ]
  );

  // Opens the create/edit modal when arriving here via a deep-link (e.g. from the Deal Summary card), then clears the params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION);
    const equipmentId = params.get(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID);
    if (!action) return;

    if (action === DEAL_BUILDER_EQUIPMENT_ACTION_ADD) {
      handleAddManualUnit();
    } else if (action === DEAL_BUILDER_EQUIPMENT_ACTION_EDIT && equipmentId) {
      handleEditEquipment(equipmentId);
    }

    params.delete(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION);
    params.delete(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID);
    replace({ pathname: location.pathname, hash: location.hash, search: params.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CardContainer
      title="Add Equipment"
      subTitle="Select units from inventory or add them manually to include in this quote."
    >
      <Stack gap={2}>
        <EquipmentSearchBar onChange={search} />

        {isSearching && <Typography color="text.secondary">Searching...</Typography>}

        {!displayItems.length ? (
          !isSearching && (
            <Typography variant="subtitle2" component="span" fontWeight={500}>
              {isSearchActive
                ? 'No equipment found for this search.'
                : 'No equipment added. Search Inventory or add equipment manually.'}
            </Typography>
          )
        ) : (
          <Stack gap={1.5}>
            {displayItems.map(item => (
              <EquipmentItemCard
                key={item.equipment.id}
                item={item}
                selected={selectedId === item.equipment.id}
                onSelect={handleSelect}
                onViewUnit={handleViewUnit}
              />
            ))}
          </Stack>
        )}

        <Button size="small" startIcon={<Add />} onClick={handleAddManualUnit} sx={{ alignSelf: 'flex-start' }}>
          Add Manual Unit
        </Button>
      </Stack>

      <RenderWithCondition condition={!!selectedId}>
        <Section title="Delivery Address">
          <EquipmentDeliveryForm equipmentId={selectedId!} submitRef={deliveryAddressSubmitRef} />
        </Section>
      </RenderWithCondition>

      {navigationNode({
        error,
        isDisabled: false,
        fn: handleSaveEquipmentStep,
        isLoading: isLoading || isEquipmentSaving,
      })}
    </CardContainer>
  );
};
